"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.normalizeAppBaseAttachmentFiles = normalizeAppBaseAttachmentFiles;
exports.appBaseBindingAttachmentPath = appBaseBindingAttachmentPath;
exports.appBaseBindingSameFile = appBaseBindingSameFile;
exports.appBaseBindingPublishedImageUrl = appBaseBindingPublishedImageUrl;
function normalizeAppBaseAttachmentFiles(value) {
    if (!value || typeof value !== 'object')
        return [];
    const object = value;
    const files = object._type === 'attachment' && Array.isArray(object.files)
        ? object.files
        : typeof object.blobId === 'string' || typeof object.id === 'string'
            ? [object]
            : Array.isArray(value)
                ? value
                : Object.entries(object).map(([blobId, raw]) => ({
                    blobId,
                    ...(raw && typeof raw === 'object' ? raw : {}),
                }));
    if (files.length > 5000)
        throw new Error('Attachment cell exceeds budget');
    return files.flatMap(raw => {
        if (!raw || typeof raw !== 'object')
            return [];
        const file = raw;
        const blobId = String(file.blobId ?? file.id ?? '').trim();
        if (!blobId)
            return [];
        const size = Number(file.size);
        return [
            {
                blobId,
                id: blobId,
                name: String(file.name ?? blobId),
                mime: String(file.mime ?? ''),
                ...(Number.isFinite(size) && size >= 0 ? { size } : {}),
            },
        ];
    });
}
/** The existing attachment endpoint rechecks current route, column, row and blob membership. */
function appBaseBindingAttachmentPath(input) {
    if (![input.sourceKey, input.rowId, input.columnId, input.blobId].every(value => typeof value === 'string' && value.length > 0 && value.length <= 512))
        throw new Error('Invalid attachment reference');
    const path = [input.sourceKey, input.rowId, input.columnId, input.blobId]
        .map(encodeURIComponent)
        .join('/');
    const query = new URLSearchParams({ route: input.route });
    if (input.token)
        query.set('token', input.token);
    return `${input.basePath.replace(/\/$/, '')}/_app-attachment/${path}?${query}`;
}
/**
 * Whether two addresses deliver the same file. The ticket of an attachment
 * address changes while its file stays; `base` resolves relative addresses.
 */
function appBaseBindingSameFile(current, next, base) {
    const file = (address) => {
        const url = new URL(address, base);
        url.searchParams.delete('token');
        return url.href;
    };
    try {
        return file(current) === file(next);
    }
    catch {
        // An address that is none is not the file to keep.
        return false;
    }
}
/** Materialized responsive images may use only the configured image delivery origin/path. */
function appBaseBindingPublishedImageUrl(value, baseUrl) {
    if (!value ||
        typeof value !== 'object' ||
        !('kind' in value) ||
        value.kind !== 'responsive-image')
        return null;
    if (!baseUrl || !('src' in value) || typeof value.src !== 'string')
        throw new Error('Published image delivery is not configured');
    // A root-relative base is the explicit local-origin image proxy contract.
    const relative = baseUrl.startsWith('/') && !baseUrl.startsWith('//');
    if (relative && (!value.src.startsWith('/') || value.src.startsWith('//')))
        throw new Error('Unapproved published image URL');
    if (/[\\\s]/.test(value.src) || /%2e|%2f|%5c/i.test(value.src))
        throw new Error('Unapproved published image URL');
    const origin = 'https://binding.invalid';
    const base = new URL(baseUrl, relative ? origin : undefined);
    const url = new URL(value.src, relative ? origin : undefined);
    if (!['https:', 'http:'].includes(url.protocol) ||
        url.origin !== base.origin ||
        url.username ||
        url.password ||
        url.search ||
        url.hash ||
        !url.pathname.startsWith(base.pathname.replace(/\/$/, '') + '/'))
        throw new Error('Unapproved published image URL');
    return relative ? url.pathname : url.toString();
}
